import { useEffect, useState } from "react";
import { THEME_TOKENS, DEFAULT_THEME_KEY, getPreset } from "./themePresets";

/**
 * App theme store.
 *
 * Holds the active preset key, persists it, and writes the preset's tokens to
 * the document root as `--app-*` CSS variables. A white-label brand color (when
 * provided) overrides `--app-primary` on top of the preset. Components read the
 * tokens, so a change here re-skins the whole app.
 *
 * Kept as a tiny module store (not redux) so it can apply tokens before/outside
 * React and stay in sync across the tree, mirroring the existing dock stores.
 */
const STORAGE_KEY = "appTheme";

const loadKey = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME_KEY;
  } catch {
    return DEFAULT_THEME_KEY;
  }
};

let state = { key: loadKey() };
const listeners = new Set();

// Applies the active preset's tokens (+ optional brand primary) to :root.
export const applyTheme = (key = state.key, brandPrimary = null) => {
  if (typeof document === "undefined") return;
  const preset = getPreset(key);
  const root = document.documentElement;
  THEME_TOKENS.forEach((t) => {
    root.style.setProperty(`--app-${t}`, preset.tokens[t]);
  });
  // White-label brand color wins for the primary action color.
  if (brandPrimary) root.style.setProperty("--app-primary", brandPrimary);
  root.setAttribute("data-theme", key);
};

export const appTheme = {
  get: () => state,
  getScheme: () => getPreset(state.key).scheme,
  set: (key) => {
    state = { key };
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* ignore */
    }
    listeners.forEach((l) => l(state));
  },
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useAppTheme() {
  const [snap, setSnap] = useState(state);
  useEffect(() => {
    setSnap(state);
    return appTheme.subscribe(setSnap);
  }, []);
  return [snap.key, appTheme.set];
}
